<template>
  <div class="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
    <!-- Header -->
    <header class="bg-white">
      <div class="max-w-6xl mx-auto px-4 py-8 text-center">
        <h1 class="text-4xl font-bold text-gray-800 mb-2">🌟 幼兒園專欄 🌟</h1>
        <p class="text-lg text-gray-600">陪伴孩子快樂成長的教育寶典</p>
      </div>
    </header>

    <!-- Main Content -->
    <main class="max-w-6xl mx-auto px-4 py-8 -mb-3">
      <!-- Welcome Section -->
      <section class="bg-white rounded-2xl shadow-lg p-8 mb-8 text-center">
        <p class="text-gray-600 leading-relaxed">
          在這裡，我們將分享幼兒教育的最新資訊、教學心得與家長小撇步，
          從生活適應到學習啟蒙，從健康飲食到情緒管理，陪伴每一個孩子快樂成長。
        </p>
      </section>

      <!-- Articles Grid -->
      <section class="mb-8">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <article
            v-for="article in currentArticles"
            :key="article.id"
            class="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
          >
            <div class="aspect-video bg-gradient-to-r from-green-100 to-blue-100 flex items-center justify-center">
              <img :src="article.image" :alt="article.title" class="w-full h-full object-cover" />
            </div>
            <div class="p-6">
              <div class="flex items-center gap-4 text-sm text-gray-500 mb-3">
                <div class="flex items-center gap-1">
                  <Calendar class="w-4 h-4" />
                  <span>{{ article.date }}</span>
                </div>
                <div class="flex items-center gap-1">
                  <User class="w-4 h-4" />
                  <span>{{ article.author }}</span>
                </div>
                <div class="flex items-center gap-1">
                  <Tag class="w-4 h-4" />
                  <span class="bg-[#FCE4DE] text-[#E9967A] px-2 py-1 rounded-full text-xs">
                    {{ article.category }}
                  </span>
                </div>
              </div>
              <h3 class="text-xl font-bold text-gray-800 mb-3 hover:text-green-600 transition-colors">
                {{ article.title }}
              </h3>
              <p class="text-gray-600 leading-relaxed mb-4">{{ article.summary }}</p>
              <button class="bg-[#E9967A] hover:bg-[#e27856] text-white px-4 py-2 rounded-lg transition-colors">
                閱讀更多
              </button>
            </div>
          </article>
        </div>
      </section>

      <!-- Pagination -->
      <section class="flex justify-center items-center gap-2 mb-8">
        <button
          @click="handlePageChange(currentPage - 1)"
          :disabled="currentPage === 1"
          :class="[
            'flex items-center gap-2 px-4 py-2 rounded-lg transition-colors',
            currentPage === 1
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-white text-[#E9967A] hover:bg-[#FCE4DE] shadow-md'
          ]"
        >
          <ChevronLeft class="w-4 h-4" />
          上一頁
        </button>

        <div class="flex gap-2">
          <button
            v-for="page in totalPages"
            :key="page"
            @click="handlePageChange(page)"
            :class="[
              'w-10 h-10 rounded-lg transition-colors',
              currentPage === page
                ? 'bg-[#E9967A] text-white shadow-lg'
                : 'bg-white text-[#E9967A] hover:bg-[#FCE4DE] shadow-md'
            ]"
          >
            {{ page }}
          </button>
        </div>

        <button
          @click="handlePageChange(currentPage + 1)"
          :disabled="currentPage === totalPages"
          :class="[
            'flex items-center gap-2 px-4 py-2 rounded-lg transition-colors',
            currentPage === totalPages
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-white text-[#E9967A] hover:bg-[#FCE4DE] shadow-md'
          ]"
        >
          下一頁
          <ChevronRight class="w-4 h-4" />
        </button>
      </section>

      <!-- Page Info -->
      <div class="text-center text-gray-500 text-sm">
        第 {{ currentPage }} 頁，共 {{ totalPages }} 頁 | 總計 {{ articles.length }} 篇文章
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'


const articles = ref([
{
      id: 1,
      title: '幼兒園生活適應小技巧',
      summary: '幫助孩子順利適應幼兒園生活的幾個實用方法，包含情緒安撫、作息調整與社交技巧培養。',
      content: '新生入園是每個家庭的重要里程碑，透過適當的準備與技巧，可以讓孩子更快樂地適應新環境...',
      date: '2024-06-01',
      author: '張老師',
      category: '生活適應',
      image: 'https://images.unsplash.com/photo-1587654780291-39c9404d746b?w=400&h=250&fit=crop'
    },
    {
      id: 2,
      title: '親子共讀的重要性',
      summary: '親子共讀對幼兒語言發展的影響與建議書單，建立良好的閱讀習慣從小開始。',
      content: '研究顯示，親子共讀不僅能促進語言發展，更能增進親子關係...',
      date: '2024-05-25',
      author: '李老師',
      category: '教育方法',
      image: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=400&h=250&fit=crop'
    },
    {
      id: 3,
      title: '健康飲食從小做起',
      summary: '幼兒園健康飲食的原則與簡單食譜分享，培養孩子良好的飲食習慣。',
      content: '均衡營養是孩子成長的重要基礎，透過有趣的方式讓孩子愛上健康食物...',
      date: '2024-05-18',
      author: '王老師',
      category: '健康飲食',
      image: 'https://images.unsplash.com/photo-1567306226416-28f0efdc88ce?w=400&h=250&fit=crop'
    },
    {
      id: 4,
      title: '創意手工DIY活動',
      summary: '適合幼兒的簡單手工製作，培養創造力與手眼協調能力。',
      content: '透過動手做的過程，孩子不僅能發揮創意，還能訓練精細動作...',
      date: '2024-05-10',
      author: '陳老師',
      category: '創意活動',
      image: 'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=400&h=250&fit=crop'
    },
    {
      id: 5,
      title: '情緒管理小妙招',
      summary: '教導幼兒認識與管理情緒，建立健康的情緒表達方式。',
      content: '情緒教育是幼兒發展的重要一環，學會表達與調節情緒...',
      date: '2024-05-03',
      author: '黃老師',
      category: '情緒發展',
      image: 'https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=400&h=250&fit=crop'
    },
    {
      id: 6,
      title: '戶外遊戲的益處',
      summary: '戶外活動對幼兒身心發展的重要性，以及安全遊戲的注意事項。',
      content: '大自然是最好的教室，戶外遊戲能促進孩子的體能發展...',
      date: '2024-04-28',
      author: '林老師',
      category: '戶外活動',
      image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=400&h=250&fit=crop'
    },
    {
      id: 7,
      title: '音樂啟蒙教育',
      summary: '音樂對幼兒發展的影響，簡單的音樂遊戲與節奏訓練。',
      content: '音樂教育能促進大腦發展，提升語言能力與創造力...',
      date: '2024-04-20',
      author: '周老師',
      category: '音樂教育',
      image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=250&fit=crop'
    },
    {
      id: 8,
      title: '社交技巧培養',
      summary: '幫助孩子建立友誼，學會分享與合作的重要技巧。',
      content: '良好的社交技巧是孩子未來成功的重要基礎...',
      date: '2024-04-15',
      author: '吳老師',
      category: '社交發展',
      image: 'https://images.unsplash.com/photo-1596464716127-f2a82984de30?w=400&h=250&fit=crop'
    },
    {
      id: 9,
      title: '睡眠習慣養成',
      summary: '建立良好的睡眠習慣，確保孩子充足的休息與健康成長。',
      content: '充足的睡眠對幼兒的身心發展至關重要...',
      date: '2024-04-08',
      author: '鄭老師',
      category: '生活習慣',
      image: 'https://images.unsplash.com/photo-1541781774459-bb2af2f05b55?w=400&h=250&fit=crop'
    },
    {
      id: 10,
      title: '數學概念啟蒙',
      summary: '透過生活化的方式，讓孩子自然地接觸數學概念。',
      content: '數學學習可以很有趣，從日常生活中培養數感...',
      date: '2024-04-01',
      author: '蘇老師',
      category: '數學啟蒙',
      image: 'https://images.unsplash.com/photo-1509869175650-a1d97972541a?w=400&h=250&fit=crop'
    },
    {
      id: 11,
      title: '藝術創作樂趣',
      summary: '培養孩子對藝術的興趣，透過繪畫與創作表達內心想法。',
      content: '藝術創作能激發孩子的想像力與創造力...',
      date: '2024-03-25',
      author: '馬老師',
      category: '藝術教育',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=250&fit=crop'
    },
    {
      id: 12,
      title: '自理能力培養',
      summary: '教導孩子基本的自理技能，建立獨立自主的能力。',
      content: '自理能力的培養需要耐心與循序漸進的引導...',
      date: '2024-03-18',
      author: '孫老師',
      category: '自理能力',
      image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=250&fit=crop'
    }
])

const currentPage = ref(1)
const articlesPerPage = 10

const totalPages = computed(() => Math.ceil(articles.value.length / articlesPerPage))

const currentArticles = computed(() => {
  const start = (currentPage.value - 1) * articlesPerPage
  return articles.value.slice(start, start + articlesPerPage)
})

const handlePageChange = (page) => {
  currentPage.value = page
  window.scrollTo({ top: 0, behavior: 'smooth' })
}
</script>
